/**
 * #1798: which Ostendo arms the CURRENT page last read live.
 *
 * Every mounted query that touches Ostendo reports the arms it read (CROS for
 * the sales reads, CROS + COOP for a two-arm read) and what its last response
 * said: live, copy, or loading (no response yet, or the query is off). The
 * header freshness badge reads the store and says "Live" for an arm only when
 * EVERY mounted reporter touching that arm says live; one copy or loading
 * report holds the arm on the copy's date, because a page with the deliveries
 * live and the stock panel on the copy has NOT been served live for CROS. The
 * header aggregates per arm across the page's reporters; the per-panel chips
 * stay per response, so the panel that did read live still says so. Unmounting
 * withdraws the report, so the badge only ever describes the page in front of
 * the user. A tiny external store rather than a context so the pages need no
 * provider and the header slot re-renders alone.
 */
import { useEffect, useId, useSyncExternalStore } from "react";
import { isLiveUnavailable, type OstendoProvenance } from "@shared/ostendo-provenance";

export type OstendoArm = "CROS" | "COOP";
export type OstendoLiveArms = Readonly<Record<OstendoArm, boolean>>;

/**
 * #3010: `live_failed` is a copy the page did not choose: live was tried and
 * failed (the response's `ostendo_reason`), so the badge says live is
 * unavailable rather than showing a plain copy date.
 */
export type OstendoReportState = "live" | "copy" | "loading" | "live_failed";
interface Report { arms: readonly OstendoArm[]; state: OstendoReportState }

const NONE: OstendoLiveArms = Object.freeze({ CROS: false, COOP: false });
const reports = new Map<string, Report>();
const listeners = new Set<() => void>();
let snapshot: OstendoLiveArms = NONE;
let failedSnapshot: OstendoLiveArms = NONE;

/** An arm is live when at least one reporter touches it and none of them says copy or loading. */
function armLive(arm: OstendoArm): boolean {
  let touched = false;
  for (const r of reports.values()) {
    if (!r.arms.includes(arm)) continue;
    touched = true;
    if (r.state !== "live") return false;
  }
  return touched;
}

/** #3010: an arm is live-unavailable when any reporter touching it got the copy because live failed. */
function armLiveFailed(arm: OstendoArm): boolean {
  for (const r of reports.values()) if (r.arms.includes(arm) && r.state === "live_failed") return true;
  return false;
}

function settle(prev: OstendoLiveArms, cros: boolean, coop: boolean): OstendoLiveArms {
  if (cros === prev.CROS && coop === prev.COOP) return prev;
  return cros || coop ? Object.freeze({ CROS: cros, COOP: coop }) : NONE;
}

function recompute() {
  const next = settle(snapshot, armLive("CROS"), armLive("COOP"));
  const nextFailed = settle(failedSnapshot, armLiveFailed("CROS"), armLiveFailed("COOP"));
  if (next !== snapshot || nextFailed !== failedSnapshot) {
    snapshot = next;
    failedSnapshot = nextFailed;
    for (const l of listeners) l();
  }
}

/** Record one reporter's arms and state, or withdraw it (no arms). */
export function reportOstendoLiveArms(id: string, arms: readonly OstendoArm[], state: OstendoReportState = "live"): void {
  if (arms.length) reports.set(id, { arms, state });
  else reports.delete(id);
  recompute();
}

export function subscribeOstendoLiveArms(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getOstendoLiveArms(): OstendoLiveArms {
  return snapshot;
}

export function getOstendoLiveFailedArms(): OstendoLiveArms {
  return failedSnapshot;
}

/** Test seam: forget every report. */
export function resetOstendoLiveArms(): void {
  reports.clear();
  recompute();
}

/** The report state a response's provenance maps to. */
export function reportStateOf(provenance: OstendoProvenance | null | undefined): OstendoReportState {
  if (provenance?.ostendo_source === "live") return "live";
  if (isLiveUnavailable(provenance)) return "live_failed";
  if (provenance?.ostendo_source === "copy") return "copy";
  return "loading";
}

export interface ReportOptions {
  /**
   * #3010 panel round: for an AUXILIARY read that is not the page's main
   * surface (the pipeline form's customer suggestions). With no response yet
   * (idle, disabled, loading, failed) the report is WITHDRAWN rather than
   * held as loading, so the side read cannot pin the page's arm on the copy
   * date or keep it from saying Live. Once it has a response it reports like
   * any other reader, so a copy answer still stops the arm reading Live
   * (#1798: Live only when every reporter says live).
   */
  onlyWhenAnswered?: boolean;
}

/**
 * Called by a page beside its query with the last RESPONSE (it carries
 * `ostendo_source` and, #3010, `ostendo_reason`): reports `arms` while
 * mounted (undefined or null = loading, which holds the arm on the copy's
 * date). Pass undefined when the query is switched off so a disabled
 * observer's retained data is not reported (the cockpit leaving trace mode).
 * Unmount withdraws the report.
 */
export function useReportOstendoSource(
  provenance: OstendoProvenance | null | undefined,
  arms: readonly OstendoArm[],
  opts: ReportOptions = {},
): void {
  const id = useId();
  const state = reportStateOf(provenance);
  const withdrawn = opts.onlyWhenAnswered === true && state === "loading";
  const armsKey = withdrawn ? "" : arms.join(",");
  useEffect(() => {
    const list = armsKey ? (armsKey.split(",") as OstendoArm[]) : [];
    reportOstendoLiveArms(id, list, state);
    return () => reportOstendoLiveArms(id, []);
  }, [id, state, armsKey]);
}

/** Read by the header badge. */
export function useOstendoLiveArms(): OstendoLiveArms {
  return useSyncExternalStore(subscribeOstendoLiveArms, getOstendoLiveArms, getOstendoLiveArms);
}

/** #3010: the arms the current page wanted live and got the copy for. */
export function useOstendoLiveFailedArms(): OstendoLiveArms {
  return useSyncExternalStore(subscribeOstendoLiveArms, getOstendoLiveFailedArms, getOstendoLiveFailedArms);
}
